import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

export function Card({ title, action, children }: { title?: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="card">
      {title ? (
        <div className="card-head">
          <h2>{title}</h2>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="stat">
      <div className="n">{value}</div>
      <div className="l">{label}</div>
    </div>
  );
}

export function Status({ online, status }: { online?: boolean; status?: string }) {
  if (online === true) return <span className="badge ok">Online</span>;
  if (online === false) return <span className="badge danger">Offline</span>;
  return <span className="badge">{status ?? 'unknown'}</span>;
}

/**
 * Error banner.
 *
 * When the API returns a troubleshooting guide (FR-B5) it is rendered as
 * steps, because "device not live" on its own leaves a venue admin with
 * nothing to do next.
 */
export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  const steps =
    error instanceof ApiError && Array.isArray((error.details as { steps?: string[] } | undefined)?.steps)
      ? ((error.details as { steps: string[] }).steps as string[])
      : null;

  return (
    <div className="notice error">
      {message}
      {steps ? (
        <ul>
          {steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Troubleshooting({ reason, steps }: { reason: string; steps: string[] }) {
  return (
    <div className="notice error">
      <strong>{reason}</strong>
      <ul>
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ul>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

export function Loading() {
  return <div className="empty">Loading…</div>;
}
